import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Gift, Copy, Share2, CheckCircle2, Send } from "lucide-react";

interface Props {
  referralLink: string;
}

export default function ReferralLinkCard({ referralLink }: Props) {
  const [inviteEmail, setInviteEmail] = useState("");
  const [copied, setCopied] = useState(false);

  const copyLink = () => {
    navigator.clipboard.writeText(`https://${referralLink}`);
    setCopied(true);
    toast.success("Referral link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareLink = () => {
    if (navigator.share) {
      navigator.share({
        title: "Join AccouPro",
        text: "I use AccouPro for my business accounting. Sign up with my link and we both get rewards!",
        url: `https://${referralLink}`,
      });
    } else {
      copyLink();
    }
  };

  const sendInvite = () => {
    if (!inviteEmail.trim() || !inviteEmail.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    toast.success(`Invite sent to ${inviteEmail}!`);
    setInviteEmail("");
  };

  return (
    <div className="p-5 rounded-lg bg-card border border-accent/30 shadow-card bg-gradient-to-br from-accent/5 to-transparent">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
          <Gift className="h-5 w-5 text-accent" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-foreground">Your Referral Link</h2>
          <p className="text-xs text-muted-foreground">Share this link and earn rewards for every signup</p>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <div className="flex-1 px-4 py-3 rounded-lg bg-muted/40 border border-border font-mono text-sm text-foreground select-all">
          https://{referralLink}
        </div>
        <Button onClick={copyLink} variant="outline" size="icon" className="h-11 w-11">
          {copied ? <CheckCircle2 className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
        </Button>
        <Button onClick={shareLink} size="icon" className="h-11 w-11">
          <Share2 className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Input
          value={inviteEmail}
          onChange={(e) => setInviteEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && sendInvite()}
          placeholder="Enter friend's email to invite"
          className="flex-1"
        />
        <Button onClick={sendInvite} className="gap-2">
          <Send className="h-4 w-4" /> Invite
        </Button>
      </div>
    </div>
  );
}
